import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { setReplicas } from './replicasStore';

import * as api from '~/api';
import { fetchPipelineDependencies } from '~/api/pipelineDependencies';
import createIconCapsule from '~/common/helpers/createIconCapsule';
import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import getBase64Url from '~/common/helpers/getBase64Url';
import { getFeatureFlag } from '~/common/helpers/globalFeatureFlags';
import i18n from '~/common/helpers/i18n';
import { Connector, Connectors } from '~/scenes/Build/helpers/getConnector';
import { clearGetConnectorLookupCache } from '~/scenes/Build/helpers/getConnectorLookupCache';

export type ConnectorComponent = {
  name: string;
  category?: string;
  jsonExample?: unknown;
  jsonSchema?: unknown[];
  visual?: unknown;
  deprecated?: boolean;
  title?: string;
  type?: string;
  iconURL?: string | null;
  shape?: string;
  documentationURL?: string;
  [key: string]: unknown;
};

export type CapsuleService = {
  id: string;
  name: string;
  iconName?: string;
  iconURL?: string;
  [key: string]: unknown;
};

export type CapsuleConsumer = {
  id: string;
  name: string;
  colorAccent?: string;
  colorBackground?: string;
  capsuleCollectionHeader?: { iconURL?: string };
  headerPattern?: string;
  capsuleServices: CapsuleService[];
  [key: string]: unknown;
};

export type LibraryService = {
  id: string;
  name: string;
  iconURL?: string | null;
  [key: string]: unknown;
};

export type LibraryConsumer = {
  id: string;
  name: string;
  iconURL?: string | null;
  libraryServices?: LibraryService[];
  [key: string]: unknown;
};

type FetchConnectorsParams = {
  realm: string;
  betaShapes: boolean;
  token?: string;
};

type ConnectorState = {
  connectors: Connectors;
  fullListFetching: boolean;
  fullListFetched: boolean;
  fullListFetchTimestamp?: number;
  dependenciesFetched: boolean;
  dependenciesFetching: boolean;
};

type ConnectorActions = {
  setConnectors: (connectors: Connectors) => void;
  setFullListFetching: (fetching: boolean) => void;
  setFullListFetched: (fetched: boolean) => void;
  setDependenciesFetched: (dependenciesFetched: boolean) => void;
  setDependenciesFetching: (dependenciesFetching: boolean) => void;
  fetchConnectors: (params: FetchConnectorsParams) => Promise<void>;
  fetchDependencies: (
    realm: string,
    pipelineId: string,
    token: string
  ) => Promise<void>;
  reset: () => void;
};

type ConnectorStore = ConnectorState & ConnectorActions;

const connectorsInitialState: Connectors = {
  triggers: [],
  components: [],
  capsulesConsumers: [],
  librariesConsumers: [],
  libraries: []
};

const initialState: ConnectorState = {
  connectors: connectorsInitialState,
  fullListFetching: false,
  fullListFetched: false,
  fullListFetchTimestamp: undefined,
  dependenciesFetched: false,
  dependenciesFetching: false
};

export const useConnectorStore = create<ConnectorStore>()(
  devtools(
    immer((set, get) => ({
      ...initialState,

      setConnectors: connectors => {
        clearGetConnectorLookupCache();
        set({ connectors });
      },

      setFullListFetching: fetching => set({ fullListFetching: fetching }),

      setFullListFetched: fetched =>
        set({
          fullListFetched: fetched,
          fullListFetching: false,
          fullListFetchTimestamp: fetched ? Date.now() : undefined
        }),

      setDependenciesFetched: dependenciesFetched =>
        set({ dependenciesFetched, dependenciesFetching: false }),

      setDependenciesFetching: dependenciesFetching =>
        set({ dependenciesFetching }),

      fetchDependencies: async (realm, pipelineId, token) => {
        const {
          setDependenciesFetching,
          setConnectors,
          setDependenciesFetched,
          fullListFetched
        } = get();

        if (fullListFetched) {
          return;
        }

        setDependenciesFetching(true);

        try {
          const useDirectIconUrl = getFeatureFlag(
            featureFlagConstants.BUILD_CANVAS_USE_DIRECT_ICON_URL
          );

          const response = await fetchPipelineDependencies(realm, pipelineId);

          const components = await Promise.all(
            (response.components as ConnectorComponent[]).map(
              async (component: ConnectorComponent) => {
                const iconURL = component?.iconURL;
                let resolvedIconURL: string | null;
                if (useDirectIconUrl) {
                  resolvedIconURL = iconURL || null;
                } else {
                  resolvedIconURL = iconURL
                    ? await getBase64Url(iconURL)
                    : null;
                }
                return {
                  ...component,
                  iconURL: resolvedIconURL,
                  shape:
                    component.shape === 'ellipsis' ? 'ellipse' : component.shape
                };
              }
            )
          ).catch(() => response.components as ConnectorComponent[]);

          const triggers = await Promise.all(
            (response.triggers as Connector[]).map(
              async (trigger: Connector) => {
                const iconURL = trigger?.iconURL;
                let resolvedIconURL: string | null | undefined;
                if (useDirectIconUrl) {
                  resolvedIconURL = iconURL || undefined;
                } else {
                  resolvedIconURL = iconURL
                    ? await getBase64Url(iconURL)
                    : undefined;
                }
                return { ...trigger, iconURL: resolvedIconURL };
              }
            )
          ).catch(() => response.triggers as Connector[]);

          const capsulesConsumers = await Promise.all(
            (response.capsules as unknown as CapsuleConsumer[]).map(
              async (capsule: CapsuleConsumer) => {
                const header = await getBase64Url(
                  capsule?.capsuleCollectionHeader?.iconURL,
                  token
                );
                return {
                  ...capsule,
                  headerPattern: header,
                  capsuleServices: capsule.capsuleServices.map(c => {
                    const iconURL = createIconCapsule({
                      header: header || undefined,
                      accent: capsule?.colorAccent,
                      background: capsule?.colorBackground,
                      icon: c?.iconName
                    });

                    return {
                      ...c,
                      iconURL
                    };
                  })
                };
              }
            )
          ).catch(() => response.capsules);

          const newConnectors: Connectors = {
            components: components as Connectors['components'],
            triggers: triggers as Connectors['triggers'],
            capsulesConsumers:
              capsulesConsumers as unknown as Connectors['capsulesConsumers'],
            librariesConsumers:
              response.libraryCatalogsConsumer as Connectors['librariesConsumers'],
            libraries: response.libraries as Connectors['libraries']
          };

          setDependenciesFetched(true);
          if (!fullListFetched) {
            setConnectors(newConnectors);
          }
        } catch {
          setDependenciesFetching(false);
        }
      },

      fetchConnectors: async ({ realm, betaShapes, token }) => {
        const {
          setFullListFetching,
          setFullListFetched,
          setDependenciesFetched,
          setConnectors,
          fullListFetched,
          fullListFetchTimestamp
        } = get();

        const ONE_HOUR_MS = 60 * 60 * 1000;
        const isCacheValid =
          fullListFetched &&
          fullListFetchTimestamp !== undefined &&
          Date.now() - fullListFetchTimestamp < ONE_HOUR_MS; // Cache is valid for 1 hour

        if (isCacheValid) {
          return;
        }

        setFullListFetching(true);

        const useDirectIconUrl = getFeatureFlag(
          featureFlagConstants.BUILD_CANVAS_USE_DIRECT_ICON_URL
        );

        const response = await api.connectors.get({ realm, betaShapes });

        const components = await Promise.all(
          response?.data?.components?.map(
            async (connectorComponent: ConnectorComponent) => {
              const iconURL = connectorComponent?.iconURL;
              let resolvedIconURL: string | null;
              if (useDirectIconUrl) {
                resolvedIconURL = iconURL || null;
              } else {
                resolvedIconURL = iconURL ? await getBase64Url(iconURL) : null;
              }
              return {
                ...connectorComponent,
                iconURL: resolvedIconURL,
                shape:
                  connectorComponent.shape === 'ellipsis'
                    ? 'ellipse'
                    : connectorComponent.shape
              };
            }
          )
        ).catch(() => response?.data?.components);

        // BACKEND-API-TRIGGER: remove synthetic `api` clone when catalog includes `api` trigger.
        const catalogTriggers = response?.data?.triggers ?? [];
        const restTrigger = catalogTriggers.find(
          ({ name }: { name: string }) => name === 'rest'
        );
        const restTriggerSchema = restTrigger?.jsonSchema?.[0];
        const apiTriggerSchema = restTriggerSchema?.schema?.filter(
          ({ property }: { property: string }) =>
            !['methods', 'uris', 'advanced'].includes(property)
        );

        const apiTrigger =
          restTrigger && restTriggerSchema
            ? {
                ...restTrigger,
                documentationURL: i18n.t('action.trigger_api_doc'),
                name: 'api',
                jsonSchema: [
                  {
                    ...restTriggerSchema,
                    schema: apiTriggerSchema ?? []
                  }
                ]
              }
            : null;

        const triggersWithApiTrigger = apiTrigger
          ? [...catalogTriggers, apiTrigger]
          : catalogTriggers;
        const triggers = (await Promise.all(
          triggersWithApiTrigger?.map(async (trigger: Connector) => {
            const iconURL = trigger?.iconURL;
            let resolvedIconURL: string | null | undefined;
            if (useDirectIconUrl) {
              resolvedIconURL = iconURL || undefined;
            } else {
              resolvedIconURL = iconURL
                ? await getBase64Url(iconURL)
                : undefined;
            }

            return {
              ...trigger,
              iconURL: resolvedIconURL
            };
          })
        ).catch(() => triggersWithApiTrigger as Connector[])) as Connector[];

        const capsulesConsumers = await Promise.all(
          response?.data?.capsulesConsumers?.map(
            async (capsuleConsumer: CapsuleConsumer) => {
              const header = await getBase64Url(
                capsuleConsumer?.capsuleCollectionHeader?.iconURL,
                token
              );
              return {
                ...capsuleConsumer,
                headerPattern: header,
                capsuleServices: capsuleConsumer.capsuleServices.map(
                  capsule => {
                    const iconURL = createIconCapsule({
                      header: header || undefined,
                      accent: capsuleConsumer?.colorAccent,
                      background: capsuleConsumer?.colorBackground,
                      icon: capsule?.iconName
                    });

                    return {
                      ...capsule,
                      iconURL
                    };
                  }
                )
              };
            }
          )
        ).catch(() => response?.data?.capsulesConsumers);

        const librariesConsumers = await Promise.all(
          response?.data?.librariesConsumers?.map(
            async (c: LibraryConsumer) => {
              const resolvedIconURL = useDirectIconUrl
                ? c?.iconURL || null
                : (await getBase64Url(c?.iconURL)) || null;
              return {
                ...c,
                iconURL: resolvedIconURL,
                libraryServices: c?.libraryServices?.map(l => ({
                  ...l,
                  iconURL: resolvedIconURL
                }))
              };
            }
          )
        ).catch(() => response?.data?.librariesConsumers);

        const libraries = response?.data?.libraries;
        const replicas = response?.data?.replicas;

        const connectors: Connectors = {
          components,
          triggers,
          capsulesConsumers,
          librariesConsumers,
          libraries
        };

        setConnectors(connectors);
        setReplicas(
          (replicas ?? []).map(({ label }: { label: string }) => ({
            label,
            value: label
          }))
        );
        setFullListFetched(true);
        setDependenciesFetched(true);
      },

      reset: () => {
        clearGetConnectorLookupCache();
        set({ ...initialState });
      }
    })),
    { name: 'ConnectorStore' }
  )
);

export const fetchConnectors = (params: FetchConnectorsParams) =>
  useConnectorStore.getState().fetchConnectors(params);

export const setConnectors = (
  connectors: Connectors,
  fullListFetched?: boolean
) => {
  useConnectorStore.getState().setConnectors(connectors);
  if (fullListFetched !== undefined) {
    useConnectorStore.getState().setFullListFetched(fullListFetched);
  }
};
